import { db } from "../config";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  runTransaction,
  orderBy,
} from "firebase/firestore";
import { createNotification } from "./notificationServices";

const GOALS_COLLECTION = "UserGoals";

// Add this new helper function
const calculateInitialProgress = async (userId, goalData) => {
  try {
    const startDate = new Date(goalData.startDate);
    const endDate = new Date(goalData.endDate);

    if (goalData.type === "books") {
      const q = query(
        collection(db, "UserBooks"),
        where("userId", "==", userId),
        where("status", "==", "Read"),
        where("updatedAt", ">=", startDate.toISOString()),
        where("updatedAt", "<=", endDate.toISOString())
      );

      const snapshot = await getDocs(q);
      let booksCount = 0;

      for (const _doc of snapshot.docs) {
        booksCount++;
      }

      return {
        books: booksCount,
        pages: 0,
      };
    } else {
      // For page goals, use ReadingProgress collection
      const q = query(
        collection(db, "ReadingProgress"),
        where("userId", "==", userId),
        where("readDate", ">=", startDate.toISOString()),
        where("readDate", "<=", endDate.toISOString())
      );

      const snapshot = await getDocs(q);
      let totalPagesRead = 0;

      // Calculate total pages read from all reading sessions
      for (const doc of snapshot.docs) {
        const progressData = doc.data();
        const pagesInSession =
          progressData.currentPage - (progressData.previousPage || 0);
        if (pagesInSession > 0) {
          totalPagesRead += pagesInSession;
        }
      }

      return {
        books: 0, // Not needed for page goals
        pages:
          totalPagesRead > goalData.target ? goalData.target : totalPagesRead,
      };
    }
  } catch (error) {
    console.error("Error calculating initial progress:", error);
    throw error;
  }
};

// Create or update a reading goal
export const setReadingGoal = async (userId, goalData) => {
  try {
    const goalRef = doc(collection(db, GOALS_COLLECTION));

    // Calculate initial progress based on existing read books
    const initialProgress = await calculateInitialProgress(userId, goalData);

    const progress =
      goalData.type === "books" ? initialProgress.books : initialProgress.pages;

    const newGoalData = {
      userId,
      title: goalData.title,
      type: goalData.type,
      target: goalData.target,
      progress: progress,
      startDate: goalData.startDate,
      endDate: goalData.endDate,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: progress >= goalData.target ? "completed" : "active",
      isPublic: goalData.isPublic ?? true,
    };

    await setDoc(goalRef, newGoalData);

    return { id: goalRef.id, success: true };
  } catch (error) {
    console.error("Error setting reading goal:", error);
    throw error;
  }
};

// Get all active goals for a user
export const getUserGoals = async (userId, viewerId = null) => {
  try {
    let q;
    if (userId === viewerId) {
      // User viewing their own goals - get all goals
      q = query(
        collection(db, GOALS_COLLECTION),
        where("userId", "==", userId),
        orderBy("status", "asc"), // Active goals first
        orderBy("updatedAt", "desc") // Newest first within each status
      );
    } else {
      // Other user viewing - only show public goals
      q = query(
        collection(db, GOALS_COLLECTION),
        where("userId", "==", userId),
        where("isPublic", "==", true),
        orderBy("status", "asc"), // Active goals first
        orderBy("updatedAt", "desc") // Newest first within each status
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting user goals:", error);
    throw error;
  }
};

// Get all goals (including completed/abandoned) for a user
export const getAllUserGoals = async (userId) => {
  try {
    const q = query(
      collection(db, GOALS_COLLECTION),
      where("userId", "==", userId),
      orderBy("endDate", "desc")
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting all user goals:", error);
    throw error;
  }
};

// Update goal progress
export const updateGoalProgress = async (userId, progressData) => {
  try {
    // Get all active goals for the user
    const activeGoals = await getUserGoals(userId, userId);

    // Filter goals by type AND status
    const relevantGoals = activeGoals.filter(
      (goal) =>
        goal.type === progressData.type &&
        (goal.status === "active" || !goal.status) // Only update active goals
    );

    for (const goal of relevantGoals) {
      const goalRef = doc(db, GOALS_COLLECTION, goal.id);

      await runTransaction(db, async (transaction) => {
        const goalDoc = await transaction.get(goalRef);

        if (!goalDoc.exists()) {
          throw new Error("Goal not found");
        }

        const currentGoal = goalDoc.data();

        // Calculate new progress
        const newProgress = Math.max(
          0,
          Math.min(
            currentGoal.progress + progressData.amount,
            currentGoal.target
          )
        );

        const updates = {
          progress: newProgress,
          updatedAt: new Date().toISOString(),
        };

        // Auto-update status if goal is completed
        if (newProgress >= currentGoal.target) {
          updates.status = "completed";
          await createNotification({
            userId: userId,
            type: "READING_GOAL_ACHIEVED",
            sourceId: goal.id,
            message: `${currentGoal.title} goal completed!`,
          });
        }

        transaction.update(goalRef, updates);
      });
    }

    return { success: true };
  } catch (error) {
    console.error("Error updating goal progress:", error);
    throw error;
  }
};

// Update goal status (abandon/reactivate)
export const updateGoalStatus = async (userId, goalId, status) => {
  try {
    const goalRef = doc(db, GOALS_COLLECTION, goalId);
    const goalDoc = await getDoc(goalRef);

    if (!goalDoc.exists() || goalDoc.data().userId !== userId) {
      throw new Error("Unauthorized");
    }

    await updateDoc(goalRef, {
      status,
      updatedAt: new Date().toISOString(),
    });

    return { success: true };
  } catch (error) {
    console.error("Error updating goal status:", error);
    throw error;
  }
};

export const updateGoal = async (userId, goalId, goalData) => {
  try {
    // First calculate the initial progress for the updated date range
    const initialProgress = await calculateInitialProgress(userId, {
      ...goalData,
      startDate: goalData.startDate,
      endDate: goalData.endDate,
    });

    // Set the progress based on goal type
    const progress =
      goalData.type === "books" ? initialProgress.books : initialProgress.pages;

    // Update the goal with new data and recalculated progress
    const goalRef = doc(db, GOALS_COLLECTION, goalId);
    await updateDoc(goalRef, {
      ...goalData,
      progress: progress,
      updatedAt: new Date().toISOString(),
      status:
        progress >= goalData.target ? "completed" : goalData.status || "active",
    });

    return { success: true };
  } catch (error) {
    console.error("Error updating goal:", error);
    throw error;
  }
};
